import React, { useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../contexts/ThemeContext';

const D3Background = ({ className = '', intensity = 0.4 }) => {
  const svgRef = useRef();
  const animationRef = useRef();
  const { isDarkMode } = useTheme();

  const createWaveBackground = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = window.innerWidth;
    const height = window.innerHeight;

    svg.attr('width', width).attr('height', height);

    // Create gradient definitions
    const defs = svg.append('defs');

    // Realistic 3D Steel Pipe Gradients with many layers
    const pipeGradient1 = defs.append('radialGradient')
      .attr('id', 'pipeGradient1')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');

    const pipeGradient2 = defs.append('radialGradient')
      .attr('id', 'pipeGradient2')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');

    const pipeGradient3 = defs.append('radialGradient')
      .attr('id', 'pipeGradient3')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');

    const particleGradient = defs.append('radialGradient')
      .attr('id', 'particleGradient')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');

    if (isDarkMode) {
      // Realistic Steel Pipe 1 - Multiple gradient stops for true 3D cylindrical effect
      pipeGradient1.selectAll('stop').data([
        { offset: '0%', color: 'rgba(255, 255, 255, 1.0)', opacity: 1.0 }, // Center bright highlight
        { offset: '15%', color: 'rgba(220, 220, 220, 1.0)', opacity: 1.0 }, // Near center light
        { offset: '25%', color: 'rgba(190, 190, 190, 1.0)', opacity: 1.0 }, // Upper light area
        { offset: '40%', color: 'rgba(160, 160, 160, 1.0)', opacity: 1.0 }, // Mid tone
        { offset: '55%', color: 'rgba(130, 130, 130, 1.0)', opacity: 1.0 }, // Shadow begins
        { offset: '70%', color: 'rgba(100, 100, 100, 1.0)', opacity: 1.0 }, // Deep shadow
        { offset: '85%', color: 'rgba(80, 80, 80, 1.0)', opacity: 1.0 }, // Darkest shadow
        { offset: '100%', color: 'rgba(60, 60, 60, 1.0)', opacity: 1.0 } // Edge shadow
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);

      pipeGradient2.selectAll('stop').data([
        { offset: '0%', color: 'rgba(240, 240, 240, 0.9)', opacity: 1.0 }, // Center highlight
        { offset: '18%', color: 'rgba(200, 200, 200, 0.9)', opacity: 1.0 }, // Light area
        { offset: '30%', color: 'rgba(170, 170, 170, 0.9)', opacity: 1.0 }, // Mid light
        { offset: '45%', color: 'rgba(140, 140, 140, 0.9)', opacity: 1.0 }, // Mid tone
        { offset: '60%', color: 'rgba(110, 110, 110, 0.9)', opacity: 1.0 }, // Shadow
        { offset: '80%', color: 'rgba(90, 90, 90, 0.9)', opacity: 1.0 }, // Deep shadow
        { offset: '100%', color: 'rgba(70, 70, 70, 0.9)', opacity: 1.0 } // Edge
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);

      pipeGradient3.selectAll('stop').data([
        { offset: '0%', color: 'rgba(230, 230, 230, 0.8)', opacity: 1.0 }, // Center
        { offset: '20%', color: 'rgba(190, 190, 190, 0.8)', opacity: 1.0 }, // Light
        { offset: '35%', color: 'rgba(160, 160, 160, 0.8)', opacity: 1.0 }, // Mid
        { offset: '50%', color: 'rgba(130, 130, 130, 0.8)', opacity: 1.0 }, // Shadow
        { offset: '70%', color: 'rgba(100, 100, 100, 0.8)', opacity: 1.0 }, // Deep shadow
        { offset: '100%', color: 'rgba(80, 80, 80, 0.8)', opacity: 1.0 } // Edge
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);

      particleGradient.selectAll('stop').data([
        { offset: '0%', color: 'rgba(192, 192, 192, 0.6)', opacity: 1.0 }, // Steel silver
        { offset: '100%', color: 'rgba(192, 192, 192, 0)', opacity: 0 }
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);
    } else {
      // Light mode - Realistic Steel Pipes with enhanced contrast
      pipeGradient1.selectAll('stop').data([
        { offset: '0%', color: 'rgba(255, 255, 255, 1.0)', opacity: 1.0 }, // Bright center
        { offset: '12%', color: 'rgba(240, 240, 240, 1.0)', opacity: 1.0 }, // Near center
        { offset: '22%', color: 'rgba(210, 210, 210, 1.0)', opacity: 1.0 }, // Light area
        { offset: '35%', color: 'rgba(180, 180, 180, 1.0)', opacity: 1.0 }, // Mid light
        { offset: '50%', color: 'rgba(150, 150, 150, 1.0)', opacity: 1.0 }, // Mid tone
        { offset: '65%', color: 'rgba(120, 120, 120, 1.0)', opacity: 1.0 }, // Shadow begins
        { offset: '80%', color: 'rgba(90, 90, 90, 1.0)', opacity: 1.0 }, // Deep shadow
        { offset: '100%', color: 'rgba(70, 70, 70, 1.0)', opacity: 1.0 } // Dark edge
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);

      pipeGradient2.selectAll('stop').data([
        { offset: '0%', color: 'rgba(250, 250, 250, 0.9)', opacity: 1.0 }, // Center
        { offset: '15%', color: 'rgba(220, 220, 220, 0.9)', opacity: 1.0 }, // Light
        { offset: '28%', color: 'rgba(190, 190, 190, 0.9)', opacity: 1.0 }, // Mid light
        { offset: '42%', color: 'rgba(160, 160, 160, 0.9)', opacity: 1.0 }, // Mid
        { offset: '58%', color: 'rgba(130, 130, 130, 0.9)', opacity: 1.0 }, // Shadow
        { offset: '75%', color: 'rgba(100, 100, 100, 0.9)', opacity: 1.0 }, // Deep shadow
        { offset: '100%', color: 'rgba(80, 80, 80, 0.9)', opacity: 1.0 } // Edge
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);

      pipeGradient3.selectAll('stop').data([
        { offset: '0%', color: 'rgba(240, 240, 240, 0.8)', opacity: 1.0 }, // Center
        { offset: '18%', color: 'rgba(210, 210, 210, 0.8)', opacity: 1.0 }, // Light
        { offset: '32%', color: 'rgba(180, 180, 180, 0.8)', opacity: 1.0 }, // Mid
        { offset: '48%', color: 'rgba(150, 150, 150, 0.8)', opacity: 1.0 }, // Shadow
        { offset: '68%', color: 'rgba(120, 120, 120, 0.8)', opacity: 1.0 }, // Deep shadow
        { offset: '100%', color: 'rgba(90, 90, 90, 0.8)', opacity: 1.0 } // Edge
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);

      particleGradient.selectAll('stop').data([
        { offset: '0%', color: 'rgba(176, 176, 176, 0.5)', opacity: 0.8 }, // Medium steel
        { offset: '100%', color: 'rgba(176, 176, 176, 0)', opacity: 0 }
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)
        .attr('stop-opacity', d => d.opacity * intensity * 2.0);
    }

    // Create wave paths
    const waveData1 = [];
    const waveData2 = [];
    const waveData3 = [];

    for (let i = 0; i <= width; i += 8) {
      waveData1.push([i, height * 0.5 + Math.sin(i * 0.01) * 80]);
      waveData2.push([i, height * 0.6 + Math.sin(i * 0.008 + Math.PI/3) * 100]);
      waveData3.push([i, height * 0.4 + Math.sin(i * 0.012 + Math.PI/6) * 60]);
    }

    const line = d3.line()
      .x(d => d[0])
      .y(d => d[1])
      .curve(d3.curveBasisOpen);

    // Realistic 3D Steel Pipes with rich radial gradients
    const wave1 = svg.append('path')
      .datum(waveData1)
      .attr('fill', 'none')
      .attr('stroke', 'url(#pipeGradient1)')
      .attr('stroke-width', 70)
      .attr('stroke-linecap', 'round')
      .attr('d', line);

    const wave2 = svg.append('path')
      .datum(waveData2)
      .attr('fill', 'none')
      .attr('stroke', 'url(#pipeGradient2)')
      .attr('stroke-width', 60)
      .attr('stroke-linecap', 'round')
      .attr('d', line);

    const wave3 = svg.append('path')
      .datum(waveData3)
      .attr('fill', 'none')
      .attr('stroke', 'url(#pipeGradient3)')
      .attr('stroke-width', 50)
      .attr('stroke-linecap', 'round')
      .attr('d', line);

    // Create animated particles (reduced for performance)
    const particleCount = Math.floor(width / 20);
    const particles = [];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.2
      });
    }

    const particleGroup = svg.append('g').attr('class', 'particles');

    const particleElements = particleGroup.selectAll('circle')
      .data(particles)
      .enter()
      .append('circle')
      .attr('r', d => d.radius)
      .attr('fill', 'url(#particleGradient)')
      .attr('opacity', d => d.opacity * intensity);

    // Animation function
    let time = 0;
    const animate = () => {
      time += 0.01;

      // Update wave paths
      const newWaveData1 = waveData1.map((d, i) => [
        d[0],
        height * 0.5 + Math.sin(d[0] * 0.01 + time * 2) * 80 + Math.cos(time * 1.5) * 20
      ]);

      const newWaveData2 = waveData2.map((d, i) => [
        d[0],
        height * 0.6 + Math.sin(d[0] * 0.008 + time * 1.5 + Math.PI/3) * 100 + Math.sin(time * 2) * 25
      ]);

      const newWaveData3 = waveData3.map((d, i) => [
        d[0],
        height * 0.4 + Math.sin(d[0] * 0.012 + time * 2.5 + Math.PI/6) * 60 + Math.cos(time * 1.8) * 15
      ]);

      // Update realistic 3D pipes
      wave1.datum(newWaveData1).attr('d', line);
      wave2.datum(newWaveData2).attr('d', line);
      wave3.datum(newWaveData3).attr('d', line);

      // Dynamic thickness animation for realistic pipe breathing
      const baseThickness1 = 70 + Math.sin(time * 1.2) * 18 + Math.cos(time * 2.1) * 10;
      const baseThickness2 = 60 + Math.sin(time * 1.6) * 15 + Math.cos(time * 1.8) * 8;
      const baseThickness3 = 50 + Math.sin(time * 2.0) * 12 + Math.cos(time * 1.5) * 6;

      // Update pipe thickness while maintaining 3D appearance
      wave1.attr('stroke-width', Math.max(40, baseThickness1));
      wave2.attr('stroke-width', Math.max(35, baseThickness2));
      wave3.attr('stroke-width', Math.max(30, baseThickness3));

      // Update particles
      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Wrap around edges
        if (particle.x < 0) particle.x = width;
        if (particle.x > width) particle.x = 0;
        if (particle.y < 0) particle.y = height;
        if (particle.y > height) particle.y = 0;

        // Add slight gravitational pull toward waves
        const waveY = height * 0.5 + Math.sin(particle.x * 0.01 + time * 2) * 40;
        const distanceToWave = Math.abs(particle.y - waveY);
        if (distanceToWave < 100) {
          particle.vy += (waveY - particle.y) * 0.0001;
        }
      });

      particleElements
        .attr('cx', d => d.x)
        .attr('cy', d => d.y);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      svg.attr('width', newWidth).attr('height', newHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode, intensity]);

  useEffect(() => {
    const cleanup = createWaveBackground();
    return cleanup;
  }, [createWaveBackground]);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <svg
        ref={svgRef}
        className="absolute inset-0 w-full h-full"
        style={{
          mixBlendMode: isDarkMode ? 'luminosity' : 'overlay',
          filter: 'blur(0px)'
        }}
      />
    </div>
  );
};

export default D3Background;